import { createTheme } from '@mui/material/styles';

// Uygulamanın ana tema ayarları — "D · Marka Renkleri" tasarımı
// Renkler ARCA Aero logosundan: lacivert #183860, kırmızı #F80000.
// Kırmızı yalnızca vurgu ve uyarı için; yapı lacivert, zemin açık.
const BRAND_NAVY = '#183860';
const BRAND_RED = '#F80000';
const BORDER = '#DCE1E8';

export const STATUS = {
  ok: { bg: '#E3F3E8', fg: '#14532D', main: '#1B6B3A' },
  warn: { bg: '#FFF1DB', fg: '#7A4D00', main: '#C05600' },
  bad: { bg: '#FEE6E6', fg: '#B30000', main: '#D40000' },
  info: { bg: '#E8F0FA', fg: '#183860', main: '#183860' },
  neutral: { bg: '#E8ECF1', fg: '#4A5A73', main: '#5B6577' },
};

// Grafik serileri için sıralı palet: lacivert öncelikli, sonra durum tonları
export const CHART_COLORS = [BRAND_NAVY, '#4A7FC1', '#1B6B3A', '#C05600', '#8A99B0', '#D40000'];

const theme = createTheme({
  palette: {
    mode: 'light',

    // Ana renk: marka laciverti (butonlar, aktif menü, başlıklar)
    primary: {
      main: BRAND_NAVY,
      light: '#2B5A94',
      dark: '#0F2544',
      contrastText: '#FFFFFF',
    },
    // İkincil: sakin yeşil (Excel butonu vb.) — neon değil
    secondary: {
      main: '#1B6B3A',
      light: '#2E8B57',
      dark: '#14532D',
      contrastText: '#FFFFFF',
    },
    // Uyarı ve hata: marka kırmızısına yakın ama metinde okunur ton
    error: {
      main: '#D40000',
      light: '#F25C5C',
      dark: '#B30000',
    },
    warning: {
      main: '#C05600',
      light: '#E08A3C',
      dark: '#8A3B00',
    },
    success: {
      main: '#1B6B3A',
      light: '#2E8B57',
      dark: '#14532D',
    },
    info: {
      main: '#1E4E8C',
      light: '#4A7FC1',
      dark: '#163A69',
    },
    // Zeminler
    background: {
      default: '#F3F5F8', // Sayfa zemini (açık, serin gri)
      paper: '#FFFFFF',   // Kart, modal, form zemini
    },
    text: {
      primary: '#14213A',   // Koyu lacivert-siyah
      secondary: '#5B6577', // İkincil metin
      disabled: '#9AA3AF',
    },
    divider: BORDER,
    // Marka kırmızısı; tema anahtarı olarak erişilebilsin diye
    brand: {
      red: BRAND_RED,
      navy: BRAND_NAVY,
    },
    // Durum tonları: zemin (bg) + okunur metin (fg). Chip, tablo satırı,
    // matris hücresi ve özet kartlarında hep aynı üç-dört ton kullanılır.
    status: STATUS,
  },

  // Barlow: logonun ağır, sıkışık harflerine akraba bir karakter
  typography: {
    fontFamily: "'Barlow', 'Segoe UI', Arial, sans-serif",
    h1: { fontWeight: 700, letterSpacing: '-0.01em' },
    h2: { fontWeight: 700, letterSpacing: '-0.01em' },
    h3: { fontWeight: 700, letterSpacing: '-0.01em' },
    h4: { fontWeight: 700, letterSpacing: '-0.01em' },
    h5: { fontWeight: 700, letterSpacing: '-0.01em' },
    h6: { fontWeight: 700 },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
    button: { fontWeight: 700, letterSpacing: '0.01em' },
  },

  // Köşeler keskin: amblem kare
  shape: {
    borderRadius: 6,
  },

  // Varsayılan bileşen stillerini burada ezeriz
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 700,
          borderRadius: 6,
        },
        outlined: {
          borderColor: '#C5CDD8',
        },
      },
    },
    // Kartlar: gölge yerine 1px kenarlık
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundImage: 'none',
          boxShadow: 'none',
          border: `1px solid ${BORDER}`,
        },
      },
    },
    // AppBar ve Drawer de Paper'dır; onlarda sadece tek kenar çizgisi kalsın
    MuiAppBar: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          borderWidth: '0 0 1px 0',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          borderWidth: '0 1px 0 0',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          boxShadow: '0 16px 48px rgba(20, 33, 58, 0.18)',
        },
      },
    },
    // Tablolar
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #E8ECF1',
          fontSize: 14,
        },
        head: {
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: '#4A5A73',
          backgroundColor: '#F3F5F8',
          borderBottom: `1px solid ${BORDER}`,
        },
      },
    },
    // Satır hover: uzun tablolarda göz kaybolmasın
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&.MuiTableRow-hover:hover': { backgroundColor: '#F7F9FC' },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          // stickyHeader başlıkları için zemin
          '& .MuiTableCell-stickyHeader': { backgroundColor: '#F3F5F8' },
        },
      },
    },
    MuiSkeleton: {
      defaultProps: { animation: 'wave' },
      styleOverrides: {
        root: { backgroundColor: '#E8ECF1' },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { height: 6, borderRadius: 3, backgroundColor: '#E8ECF1' },
        bar: { borderRadius: 3 },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          fontWeight: 700,
        },
      },
    },
    // Filtre düğmeleri: seçili olan lacivert dolgu
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderColor: '#C5CDD8',
          '&.Mui-selected': {
            backgroundColor: BRAND_NAVY,
            color: '#FFFFFF',
            '&:hover': { backgroundColor: '#0F2544' },
          },
        },
      },
    },
    // Form alanları
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#5B6577',
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          backgroundColor: '#F3F5F8',
          borderRadius: 6,
          '&:hover': { backgroundColor: '#E9EDF2' },
          '&.Mui-focused': { backgroundColor: '#FFFFFF' },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: '#C5CDD8',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#14213A',
          fontSize: 12,
        },
      },
    },
  },
});

export default theme;
